import { IconWrapper, IStyles, pxToRem, pxTovW } from '@geneo2-web/shared-ui';
import {
  Box,
  Button,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AiGeneratedButton from './Buttons/AiGeneratedButton';
import IncludeImageButton from './Buttons/IncludeImageButton';
import { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { interactionEvent } from '../../../Auth/auth.events';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';

const styles: IStyles = {
  root: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    gap: {
      xs: pxToRem(10),
      md: pxTovW(14),
    },
  },
  leftActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
  },
  includeImageWrapper: {
    ml: 'auto',
  },
  rightActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  addAlternateButton: {
    color: '#007CDC',
    border: '1px solid #007CDC',
    borderRadius: {
      md: '16px',
      xs: '50%',
    },
    p: {
      md: '1px 8px',
      xs: '4px 4px',
    },
    cursor: 'pointer',
    background: '#E2F2FF',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  plusIcon: {
    height: '13px',
    width: '13px',
  },
  deleteButton: {
    cursor: 'pointer',
    p: 0,
    minWidth: 'unset',
    borderRadius: '50%',
    border: '1px solid transparent',
    transition:
      'transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      backgroundColor: 'transparent',
      border: '1px solid gray',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
    },
  },
  replaceButton: {
    cursor: 'pointer',
    p: 0,
  },
  moreButton: {
    p: 0,
  },
  moreIcon: {
    transform: 'rotate(90deg)',
    height: '12px',
  },
  popover: {
    '& .MuiPopover-root': {},
    '& .MuiPopover-paper': {
      borderRadius: { xs: pxToRem(10), md: pxToRem(10) },
      border: '1px solid #75C3FF',
      backgroundColor: '#F8FCFF',
    },
  },
  popoverMenuButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    borderBottom: '1px solid #F1F1F1',
    width: '100%',
    justifyContent: 'flex-start',
  },
  popoverIncludeImageButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    width: '100%',
    justifyContent: 'flex-start',
  },
  includeImageCheckbox: {
    height: '12px',
    width: '12px',
    backgroundColor: '#fff',
    borderRadius: '3px',
    border: '1px solid #007CDC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  includeImageCheckIcon: {
    height: '10px',
    width: '10px',
    color: '#000',
    stroke: '#000',
    strokeWidth: 2,
  },
};

interface InteractiveButtonsProps {
  onDeleteClick?: () => void;
  handleReplaceClick?: () => void;
  addAlternateQuestionsClick?: () => void;
  showAIButton?: boolean;
  handleAIPrompt?: (prompt: string) => void;
  showIncludeImageButton?: boolean;
  isIncludeImageChecked?: boolean;
  handleIncludeImageClick?: () => void;
  stackComponents?: boolean;
}

export const InteractiveButtons = ({
  onDeleteClick,
  handleReplaceClick,
  addAlternateQuestionsClick,
  showAIButton,
  handleAIPrompt,
  showIncludeImageButton,
  isIncludeImageChecked,
  handleIncludeImageClick,
  stackComponents,
}: InteractiveButtonsProps) => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  return (
    <Box sx={styles.root}>
      <Box sx={styles.leftActions}>
        {showAIButton && <AiGeneratedButton handlePrompt={handleAIPrompt} />}
        {showIncludeImageButton && !stackComponents && (
          <Box sx={styles.includeImageWrapper}>
            <IncludeImageButton
              isChecked={isIncludeImageChecked}
              onClick={handleIncludeImageClick}
            />
          </Box>
        )}
      </Box>
      <Box sx={styles.rightActions}>
        {addAlternateQuestionsClick && (
          <Button
            sx={styles.addAlternateButton}
            onClick={async (ev) => {
              ev.stopPropagation();
              addAlternateQuestionsClick && addAlternateQuestionsClick();
              await interactionEvent({
                context: 'Review_question_paper',
                name: 'ADD_ALTERNATE_QUESTION',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
          >
            <IconWrapper
              name="plus_icon"
              customSx={styles.plusIcon}
              type="png"
              parentFolder="icons"
            />
            {!isMobile && (
              <Typography color="#007CDC" variant="smallText">
                Add Alternate Questions
              </Typography>
            )}
          </Button>
        )}
        {onDeleteClick && !stackComponents && (
          <Button
            onClick={async () => {
              onDeleteClick();
              await interactionEvent({
                context: 'Review_question_paper',
                name: 'DELETE',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            sx={styles.deleteButton}
          >
            <IconWrapper
              name="remove"
              size="md"
              type="png"
              parentFolder="icons"
            />
          </Button>
        )}
        {handleReplaceClick && !stackComponents && (
          <Button
            onClick={async () => {
              handleReplaceClick();
              await interactionEvent({
                context: 'Review_question_paper',
                name: 'REPLACE',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            sx={styles.replaceButton}
          >
            <IconWrapper
              name="refresh"
              size="md"
              type="png"
              parentFolder="icons"
            />
          </Button>
        )}
        <IconWrapper
          name="reorder"
          size="small"
          type="png"
          parentFolder="icons"
        />
        {stackComponents && (
          <Button
            onClick={(ev) => {
              ev?.stopPropagation();
              setAnchorEl(ev.currentTarget);
            }}
            sx={styles.moreButton}
          >
            <IconWrapper
              name="triple-horizontal-dots"
              size="small"
              parentFolder="icons"
              type="png"
              customSx={styles.moreIcon}
            />
          </Button>
        )}
      </Box>
      <Popover
        id="popover-interactive-buttons"
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={styles.popover}
      >
        {onDeleteClick && (
          <Box>
            <Button
              onClick={async (event) => {
                setAnchorEl(null);
                event.stopPropagation();
                onDeleteClick();
                await interactionEvent({
                  context: 'Review_question_paper',
                  name: 'DELETE',
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
              sx={styles.popoverMenuButton}
            >
              <IconWrapper
                name="remove"
                size="md"
                type="png"
                parentFolder="icons"
              />
              <Typography variant="h4" fontWeight="medium">
                Delete
              </Typography>
            </Button>
          </Box>
        )}
        <Box>
          <Button
            onClick={async (event) => {
              setAnchorEl(null);
              event.stopPropagation();
              handleReplaceClick && handleReplaceClick();
              await interactionEvent({
                context: 'Review_question_paper',
                name: 'REPLACE',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            sx={styles.popoverMenuButton}
          >
            <IconWrapper
              name="refresh"
              size="md"
              type="png"
              parentFolder="icons"
            />
            <Typography variant="h4" fontWeight="medium">
              Replace Question
            </Typography>
          </Button>
        </Box>
        {showIncludeImageButton && (
          <Box>
            <Button
              sx={styles.popoverIncludeImageButton}
              onClick={(event) => {
                setAnchorEl(null);
                event.stopPropagation();
                handleIncludeImageClick && handleIncludeImageClick();
              }}
            >
              <Box sx={styles.includeImageCheckbox}>
                {isIncludeImageChecked && (
                  <CheckIcon sx={styles.includeImageCheckIcon} />
                )}
              </Box>
              <Typography variant="h4" fontWeight="medium">
                Include Image
              </Typography>
            </Button>
          </Box>
        )}
      </Popover>
    </Box>
  );
};
